import { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faq';
import { tagClass } from '@/utils/helpers';

const featuredId = (articles.find((a) => a.featured) || articles[0]).id;
const specials = articles.filter((a) => a.timeline && a.id !== featuredId).slice(0, 3);

const stats = [
  { label: '本周瓜讯更新', value: '120+', icon: 'ri-article-line', tint: 'text-primary-600 bg-primary-50' },
  { label: '追踪反转专题', value: '6', icon: 'ri-refresh-line', tint: 'text-accent-700 bg-accent-100' },
  { label: '热门讨论话题', value: '38', icon: 'ri-chat-3-line', tint: 'text-secondary-700 bg-secondary-100' },
  { label: '累计阅读热度', value: '2.3亿+', icon: 'ri-fire-line', tint: 'text-primary-600 bg-primary-50' },
];

export default function ReversalAndFaq() {
  const [open, setOpen] = useState<number>(0);
  return (
    <>
      {/* stats band */}
      <section className="border-y border-background-200 bg-secondary-50/50">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-2 gap-4 px-4 md:px-6 py-6 md:py-8 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-3">
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-xl ${s.tint}`}>
                <i className={s.icon} aria-hidden="true" />
              </span>
              <div>
                <p className="font-label text-xl md:text-2xl font-extrabold text-foreground-950">{s.value}</p>
                <p className="text-xs text-foreground-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* reversal special */}
      <section className="mx-auto w-full max-w-6xl px-4 md:px-6 py-10 md:py-14">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <span className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-700">
              <i className="ri-refresh-line" aria-hidden="true" /> 瓜中瓜 · 连环反转
            </span>
            <h3 className="text-xl md:text-2xl font-bold">追踪中的反转时间线</h3>
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {specials.map((a) => (
            <Link
              key={a.id}
              to={`/post/${a.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:-translate-y-1"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={a.cover}
                  alt={a.alt}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-950/60 to-transparent" />
                <span className="absolute bottom-2 left-3 inline-flex items-center gap-1 text-xs text-background-100">
                  <i className="ri-time-line text-accent-400" aria-hidden="true" /> 已追踪 {a.timeline?.length} 个节点
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className={`mb-2 inline-flex w-fit rounded-md border px-2 py-0.5 text-xs font-medium ${tagClass(a.tag)}`}>{a.tag}</span>
                <h4 className="mb-2 line-clamp-2 text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {a.title}
                </h4>
                <p className="mb-3 line-clamp-2 text-sm leading-relaxed text-foreground-600">{a.summary}</p>
                <div className="mt-auto space-y-1.5">
                  {a.timeline?.slice(-2).map((t) => (
                    <div key={t.title} className="flex items-start gap-2 text-xs text-foreground-500">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                      <span className="line-clamp-1">
                        <span className="font-medium text-foreground-700">{t.time}</span> · {t.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 md:px-6 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <span className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600">
              <i className="ri-question-line" aria-hidden="true" /> 常见问题
            </span>
            <h3 className="text-xl md:text-2xl font-bold">吃瓜前，先读懂这些</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              从"什么是吃瓜时间线"到"录音是否可信"，这里汇总了读者最常问的问题。想进一步了解我们的定位与理念，欢迎前往
              <Link to="/about" className="mx-1 font-medium text-primary-600 hover:underline">关于我们</Link>。
            </p>
            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="font-label text-sm font-bold text-foreground-950">读者口碑</p>
              <blockquote className="mt-2 text-sm leading-relaxed text-foreground-600">
                "每天睡前刷一遍时间线，像追剧一样把反转捋得清清楚楚，关键证据列得明明白白，真的是吃瓜神器。"
              </blockquote>
              <div className="mt-3 flex items-center gap-2 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill" aria-hidden="true" />
                ))}
                <span className="text-xs text-foreground-500">来自站内读者反馈</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={f.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-background-100"
                  aria-expanded={open === i}
                >
                  <span className="text-sm font-semibold text-foreground-950 md:text-base">{f.q}</span>
                  <i
                    className={`shrink-0 text-primary-600 transition-transform ${open === i ? 'ri-subtract-fill' : 'ri-add-fill'}`}
                    aria-hidden="true"
                  />
                </button>
                {open === i && (
                  <p className="px-4 pb-4 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}